/**
 * TopShelf: material-style ripple on product/resource cards.
 *
 * Reuses the `@keyframes ripple-animation` already shipped by
 * menu-drawer-kith.css, which is loaded globally via the kith header section.
 *
 * Implementation notes:
 *   - Attaches a single delegated `click` listener to document.
 *   - Injects a non-interactive ripple host once per card so we never have to
 *     mutate the Horizon card element's own position/overflow inline styles;
 *     CSS below handles positioning.
 *   - Skips clicks that land on interactive children (quick-add button,
 *     variant swatches, form controls) so those widgets keep their own
 *     feedback.
 */
(function initTopshelfRipple() {
  if (window.__topshelfRippleBound) return;
  window.__topshelfRippleBound = true;

  const CARD_SELECTOR = '.resource-card, product-card';
  const SKIP_SELECTOR = [
    'button',
    '[role="button"]',
    'input',
    'select',
    'textarea',
    'label',
    'variant-swatches',
    'quick-add',
    '.quick-add',
    '.variant-picker',
    '.product-card-actions',
    '.product-card__add-to-cart',
    '.product-form__buttons',
    'a:not(.resource-card__link):not(.product-card__link):not([ref="cardGalleryLink"]):not([ref="productTitleLink"])',
  ].join(',');

  function getRippleHost(card) {
    let host = card.querySelector(':scope > .topshelf-ripple-host');
    if (host) return host;
    host = document.createElement('span');
    host.className = 'topshelf-ripple-host';
    host.setAttribute('aria-hidden', 'true');
    card.appendChild(host);
    return host;
  }

  function spawnRipple(e, card) {
    const host = getRippleHost(card);
    host.querySelectorAll('.ripple').forEach((el) => el.remove());

    const rect = card.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height) * 0.9;
    const clientX = typeof e.clientX === 'number' ? e.clientX : rect.left + rect.width / 2;
    const clientY = typeof e.clientY === 'number' ? e.clientY : rect.top + rect.height / 2;
    const x = clientX - rect.left - size / 2;
    const y = clientY - rect.top - size / 2;

    const ripple = document.createElement('span');
    ripple.className = 'ripple';
    ripple.style.cssText = [
      `width:${size}px`,
      `height:${size}px`,
      `left:${x}px`,
      `top:${y}px`,
      'position:absolute',
      'border-radius:50%',
      'background:rgba(0,0,0,0.18)',
      'pointer-events:none',
      'transform:scale(0)',
      'transform-origin:center',
      'animation:ripple-animation 0.6s linear',
      'z-index:9999',
    ].join(';');

    host.appendChild(ripple);
    setTimeout(() => ripple.remove(), 700);
  }

  document.addEventListener(
    'click',
    (e) => {
      const card = e.target.closest(CARD_SELECTOR);
      if (!card) return;
      if (e.target.closest(SKIP_SELECTOR)) return;
      spawnRipple(e, card);
    },
    { passive: true }
  );
})();

(function initTopshelfDescriptionAutoScroll() {
  if (window.__topshelfDescriptionAutoScrollBound) return;
  window.__topshelfDescriptionAutoScrollBound = true;

  const CONTROL_SELECTOR = '.desc-accordion__toggle, .desc-accordion__icon-button';

  function getAccordion(target) {
    return target && target.closest ? target.closest('.desc-accordion') : null;
  }

  function scrollToProductTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  document.addEventListener(
    'click',
    (event) => {
      const control = event.target.closest(CONTROL_SELECTOR);
      const accordion = getAccordion(control);
      if (!accordion) return;

      const isClosing = accordion.classList.contains('is-open');
      if (!isClosing) return;

      window.setTimeout(() => {
        if (!accordion.classList.contains('is-open')) {
          scrollToProductTop();
        }
      }, 80);

      // Run once more after the accordion height transition settles so the
      // browser cannot strand the viewport near the footer after the collapse.
      window.setTimeout(() => {
        if (!accordion.classList.contains('is-open')) {
          scrollToProductTop();
        }
      }, 420);
    },
    true
  );
})();

(function initTopshelfPdpPurchaseLayout() {
  if (window.__topshelfPdpPurchaseLayoutBound) return;
  window.__topshelfPdpPurchaseLayoutBound = true;

  const ENDPOINT = 'https://storesboost-steven-beivue.laravel.cloud/api/public/wish-list';
  const TOKEN = 'PtAJM1HyT7BOFQdd3ROw1PmsrqedqTk0ZJU3DLSt';

  function getCurrentVariantGid(fallbackId) {
    const input = document.querySelector('form[action*="/cart/add"] [name="id"]');
    const id = input ? input.value : fallbackId;
    return String(id).startsWith('gid://shopify/ProductVariant/')
      ? id
      : `gid://shopify/ProductVariant/${id}`;
  }

  function attachBehavior(btn) {
    const customer = {
      id: btn.dataset.customerId,
      email: btn.dataset.customerEmail,
    };
    const fallbackVariantId = btn.dataset.variantId;

    const setButtonState = (active) => {
      btn.classList.toggle('is-active', active);
      btn.querySelector('.wishlist-text').textContent = active ? '♥ Wish Listed' : '♡ Wish List';
    };

    const readWishlist = async () => {
      try {
        const res = await fetch(`${ENDPOINT}?customer_id=${encodeURIComponent(customer.id)}`, {
          headers: { 'X-Wishlist-Token': TOKEN },
        });
        if (!res.ok) return;
        const data = await res.json();
        const gid = getCurrentVariantGid(fallbackVariantId);
        setButtonState((data?.wishlist?.variant_ids || []).includes(gid));
      } catch (e) {
        console.error(e);
      }
    };

    const toggleWishlist = async () => {
      const variant_id = getCurrentVariantGid(fallbackVariantId);
      const wasActive = btn.classList.contains('is-active');
      setButtonState(!wasActive);
      btn.disabled = true;
      try {
        const res = await fetch(`${ENDPOINT}/items`, {
          method: wasActive ? 'DELETE' : 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Wishlist-Token': TOKEN },
          body: JSON.stringify({ customer, variant_id }),
        });
        const data = await res.json();
        if (!res.ok || data?.ok === false) {
          setButtonState(wasActive);
          console.error(data);
        }
      } catch (e) {
        setButtonState(wasActive);
        console.error(e);
      } finally {
        btn.disabled = false;
      }
    };

    btn.addEventListener('click', toggleWishlist);
    readWishlist();

    document.addEventListener('change', (e) => {
      if (e.target.matches('form[action*="/cart/add"] [name="id"]')) readWishlist();
    });
  }

  function enhance() {
    document.querySelectorAll('.wishlist-button[data-customer-id]:not([data-ts-wl-bound])').forEach((btn) => {
      btn.dataset.tsWlBound = '1';
      attachBehavior(btn);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhance, { once: true });
  } else {
    enhance();
  }

  document.addEventListener('shopify:section:load', enhance);
})();
